import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Heart, Users, Shield, Sparkles, HandHeart, Clock } from "lucide-react";

const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 }
  };

  const importancePoints = [
    {
      icon: Heart,
      title: "Emotional Support for the Family",
      description: "Families often feel overwhelmed, scared, and uncertain during their loved one's final days. An end-of-life doula provides a calm, reassuring presence that helps family members process their emotions, find peace, and cherish meaningful moments together."
    },
    {
      icon: Users,
      title: "Guidance Through the Unknown",
      description: "Most families have never experienced the dying process. A death doula explains what to expect, normalizes the experience, and helps families understand the physical and emotional changes their loved one may undergo, reducing fear and anxiety."
    },
    {
      icon: Shield,
      title: "Advocacy and Communication",
      description: "Acting as a bridge between the family and medical professionals, an end-of-life doula ensures the dying person's wishes are respected and helps families navigate difficult conversations about care preferences."
    },
    {
      icon: Clock,
      title: "Continuous Presence",
      description: "Unlike medical staff who have multiple patients, an end-of-life doula provides dedicated, continuous support. This means someone is always there to hold a hand, offer comfort, or simply be a peaceful presence during vigil hours."
    },
    {
      icon: HandHeart,
      title: "Legacy Work and Memory Making",
      description: "A death doula helps families create lasting memories through legacy projects, recording stories, facilitating meaningful conversations, and ensuring that the person's life is honored and remembered."
    },
    {
      icon: Sparkles,
      title: "Grief Support for the Living",
      description: "The support doesn't end when a loved one passes. An end-of-life doula provides aftercare, helping families navigate grief, funeral planning, and the emotional journey of healing."
    }
  ];

  return (
    <section 
      id="about" 
      className="relative py-24 md:py-32 bg-[#F9F8F4]"
      ref={ref}
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={fadeInUp}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-[#8F9E82] font-bold mb-4">
            Understanding End of Life Care
          </p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#2D2A26] mb-6">
            What is a Death Doula?
          </h2>
        </motion.div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="bg-[#E8E4D9] rounded-2xl p-8">
              <h3 className="font-['Playfair_Display'] text-2xl text-[#2D2A26] mb-4">
                I Am Not a Nurse
              </h3>
              <p className="text-[#5C5852] leading-relaxed mb-4">
                It's important to understand that as an End of Life Doula, I am <strong>not a medical professional</strong>. 
                I do not provide medical care, administer medications, or make clinical decisions. 
                Instead, I offer something equally vital: <strong>human connection, emotional support, and compassionate companionship</strong>.
              </p>
              <p className="text-[#5C5852] leading-relaxed">
                While hospice nurses and doctors focus on managing physical symptoms, 
                my role is to tend to the <strong>heart and soul</strong> of both the dying person and their loved ones. 
                I fill the gaps that medical care cannot address.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-2xl p-8 shadow-[0_4px_24px_rgba(74,93,35,0.06)] border border-[#4A5D23]/10">
              <h3 className="font-['Playfair_Display'] text-2xl text-[#2D2A26] mb-4">
                A Compassionate Companion
              </h3>
              <p className="text-[#5C5852] leading-relaxed mb-4">
                A Death Doula, also known as an End-of-Life Doula or Death Midwife, is a trained professional 
                who provides <strong>non-medical support</strong> to individuals and families during the dying process 
                and beyond.
              </p>
              <p className="text-[#5C5852] leading-relaxed mb-4">
                Just as birth doulas support the journey into life, death doulas support the journey out of it. 
                We help create a <strong>peaceful, dignified, and meaningful</strong> transition for those approaching 
                the end of their lives.
              </p>
              <p className="text-[#5C5852] leading-relaxed">
                My role encompasses <strong>emotional support, practical guidance, vigil planning, 
                legacy work, and aftercare</strong> for grieving families.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Why End of Life Caretaker is Important */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={fadeInUp}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mb-12"
        >
          <div className="text-center mb-12">
            <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#2D2A26] mb-4">
              Why Families Need an End of Life Caretaker
            </h3>
            <p className="text-lg text-[#5C5852] max-w-3xl mx-auto">
              The final days, hours, and moments of a loved one's life are <strong>sacred and irreplaceable</strong>. 
              Having professional support during this time can transform a frightening experience into one 
              of profound connection and peace.
            </p>
          </div>
        </motion.div>

        {/* Importance Points Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {importancePoints.map((point, index) => (
            <motion.div
              key={point.title}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-[0_4px_24px_rgba(74,93,35,0.06)] border border-[#4A5D23]/10 hover:border-[#4A5D23]/30 transition-all duration-300 hover:-translate-y-1"
              data-testid={`importance-card-${index}`}
            >
              <div className="w-12 h-12 bg-[#4A5D23]/10 rounded-xl flex items-center justify-center mb-4">
                <point.icon className="w-6 h-6 text-[#4A5D23]" />
              </div>
              <h4 className="font-['Playfair_Display'] text-xl text-[#2D2A26] mb-3">
                {point.title}
              </h4>
              <p className="text-[#5C5852] text-sm leading-relaxed">
                {point.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Closing Statement */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={fadeInUp}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-16 text-center"
        >
          <div className="bg-[#4A5D23] text-white rounded-2xl p-8 md:p-12 max-w-4xl mx-auto">
            <p className="font-['Playfair_Display'] text-2xl md:text-3xl italic mb-4">
              "Death is not the opposite of life, but a part of it."
            </p>
            <p className="text-[#E8E4D9]">
              I am here to help you and your family navigate this sacred transition with grace, 
              dignity, and love. You don't have to do this alone.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
